import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Sparkles,
  Plus,
  Trash2,
  CheckCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  Link2,
  AlertCircle,
  ShieldAlert,
} from 'lucide-react';
import toast from 'react-hot-toast';
import securityApi from '../../api/securityApi';
import RiskMatrix from '../../components/compliance/RiskMatrix';
import CustomizationScoreBar from '../../components/compliance/CustomizationScoreBar';

const LIKELIHOOD_OPTIONS = [
  { value: 'rare', label: 'Rare' },
  { value: 'unlikely', label: 'Unlikely' },
  { value: 'possible', label: 'Possible' },
  { value: 'likely', label: 'Likely' },
  { value: 'almost_certain', label: 'Almost Certain' },
];

const IMPACT_OPTIONS = [
  { value: 'negligible', label: 'Negligible' },
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'severe', label: 'Severe' },
];

const CATEGORY_OPTIONS = [
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'access_control', label: 'Access Control' },
  { value: 'data_protection', label: 'Data Protection' },
  { value: 'operational', label: 'Operational' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'personnel', label: 'Personnel' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'application_security', label: 'Application Security' },
];

const getRiskScoreColor = (score) => {
  if (score <= 3) return 'bg-green-100 text-green-800';
  if (score <= 6) return 'bg-yellow-100 text-yellow-800';
  if (score <= 12) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

const getStatusDot = (risk) => {
  if (!risk.is_ai_generated) return { color: 'bg-purple-500', label: 'User-added' };
  if (risk.is_modified) return { color: 'bg-green-500', label: 'Modified' };
  return { color: 'bg-blue-500', label: 'AI-generated' };
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-gray-100 text-gray-700',
    in_review: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
  };
  const labels = { draft: 'Draft', in_review: 'In Review', approved: 'Approved' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
      {labels[status] || status}
    </span>
  );
};

const EMPTY_RISK = {
  title: '',
  description: '',
  category: 'operational',
  likelihood: 'possible',
  impact: 'moderate',
  mitigation_strategy: '',
  residual_risk: '',
};

const RiskAssessmentPage = () => {
  const { projectId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [expandedRiskId, setExpandedRiskId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRisk, setNewRisk] = useState({ ...EMPTY_RISK });
  const [editingFields, setEditingFields] = useState({});
  const [suggestedControls, setSuggestedControls] = useState({});

  const fetchAssessment = useCallback(async () => {
    try {
      const data = await securityApi.getRiskAssessment(projectId);
      setAssessment(data);
    } catch {
      setAssessment(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchAssessment();
  }, [fetchAssessment]);

  // Poll while generating (timeout after 2 minutes)
  useEffect(() => {
    if (!generating) return;
    const interval = setInterval(async () => {
      const data = await securityApi.getRiskAssessment(projectId);
      if (data && data.risks && data.risks.length > 0) {
        setAssessment(data);
        setGenerating(false);
      }
    }, 3000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setGenerating(false);
      toast.error('Risk generation timed out. Please try again.');
    }, 120000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [generating, projectId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await securityApi.generateRiskAssessment(projectId);
      toast.success('Risk generation started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate risks');
      setGenerating(false);
    }
  };

  const handleUpdateRisk = async (riskId, updates) => {
    try {
      const updated = await securityApi.updateRisk(projectId, riskId, updates);
      setAssessment((prev) => ({
        ...prev,
        risks: prev.risks.map((r) => (r.id === riskId ? updated : r)),
      }));
      // Refetch to get updated customization_score
      const full = await securityApi.getRiskAssessment(projectId);
      if (full) setAssessment(full);
      toast.success('Risk updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update risk');
    }
  };

  const handleDeleteRisk = async (riskId) => {
    if (!window.confirm('Delete this risk?')) return;
    try {
      await securityApi.deleteRisk(projectId, riskId);
      const full = await securityApi.getRiskAssessment(projectId);
      if (full) setAssessment(full);
      toast.success('Risk deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete risk');
    }
  };

  const handleAddRisk = async () => {
    if (!newRisk.title || !newRisk.description || !newRisk.mitigation_strategy) {
      toast.error('Title, description, and mitigation strategy are required');
      return;
    }
    try {
      await securityApi.createRisk(projectId, newRisk);
      setNewRisk({ ...EMPTY_RISK });
      setShowAddForm(false);
      const full = await securityApi.getRiskAssessment(projectId);
      if (full) setAssessment(full);
      toast.success('Risk added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add risk');
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const result = await securityApi.approveRiskAssessment(projectId);
      setAssessment(result);
      toast.success('Risk assessment approved');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.error) {
        toast.error(detail.error);
      } else {
        toast.error(detail || 'Failed to approve');
      }
    } finally {
      setApproving(false);
    }
  };

  const handleFieldChange = (riskId, field, value) => {
    setEditingFields((prev) => ({
      ...prev,
      [riskId]: { ...(prev[riskId] || {}), [field]: value },
    }));
  };

  const handleFieldBlur = async (riskId, field) => {
    const value = editingFields[riskId]?.[field];
    if (value !== undefined) {
      await handleUpdateRisk(riskId, { [field]: value });
      setEditingFields((prev) => {
        const copy = { ...prev };
        if (copy[riskId]) {
          delete copy[riskId][field];
          if (Object.keys(copy[riskId]).length === 0) delete copy[riskId];
        }
        return copy;
      });
    }
  };

  const handleLoadSuggestions = async (riskId) => {
    try {
      const suggestions = await securityApi.getSuggestedControls(projectId, riskId);
      setSuggestedControls((prev) => ({ ...prev, [riskId]: suggestions }));
    } catch {
      toast.error('Failed to load suggestions');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // No assessment yet — show generate button
  if (!assessment) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-center py-16">
          <ShieldAlert className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Risk Assessment</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Generate an AI-powered risk assessment tailored to your company's specific infrastructure, tools, and compliance posture.
          </p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {generating ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {generating ? 'Generating Risks...' : 'Generate Risk Assessment'}
          </button>
          {generating && (
            <p className="text-sm text-gray-500 mt-3">
              This may take 30-60 seconds. We're analyzing your company's context...
            </p>
          )}
        </div>
      </div>
    );
  }

  const risks = assessment.risks || [];
  const isApproved = assessment.status === 'approved';
  const canApprove = assessment.customization_score >= 0.3 && !isApproved;

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Risk Assessment</h1>
          <StatusBadge status={assessment.status} />
        </div>
        {!isApproved && (
          <button
            onClick={handleApprove}
            disabled={!canApprove || approving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={
              !canApprove && !isApproved
                ? `Customization score must be at least 30% (currently ${(assessment.customization_score * 100).toFixed(0)}%)`
                : undefined
            }
          >
            {approving ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Approve Assessment
          </button>
        )}
      </div>

      {/* Customization Score */}
      {!isApproved && (
        <div className="mb-6">
          <CustomizationScoreBar
            score={assessment.customization_score}
            threshold={0.3}
          />
        </div>
      )}

      {/* Risk Matrix */}
      <div className="mb-6">
        <RiskMatrix risks={risks} />
      </div>

      {/* Risk Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">
            Risks ({risks.length})
          </h3>
          {!isApproved && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Risk
            </button>
          )}
        </div>

        {/* Add risk form */}
        {showAddForm && (
          <div className="px-4 py-4 border-b border-gray-200 bg-purple-50">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input
                type="text"
                placeholder="Risk title"
                value={newRisk.title}
                onChange={(e) => setNewRisk((prev) => ({ ...prev, title: e.target.value }))}
                className="col-span-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              />
              <textarea
                placeholder="Description"
                value={newRisk.description}
                onChange={(e) => setNewRisk((prev) => ({ ...prev, description: e.target.value }))}
                className="col-span-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                rows={2}
              />
              <select
                value={newRisk.category}
                onChange={(e) => setNewRisk((prev) => ({ ...prev, category: e.target.value }))}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <select
                  value={newRisk.likelihood}
                  onChange={(e) => setNewRisk((prev) => ({ ...prev, likelihood: e.target.value }))}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  {LIKELIHOOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  value={newRisk.impact}
                  onChange={(e) => setNewRisk((prev) => ({ ...prev, impact: e.target.value }))}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  {IMPACT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <textarea
                placeholder="Mitigation strategy"
                value={newRisk.mitigation_strategy}
                onChange={(e) => setNewRisk((prev) => ({ ...prev, mitigation_strategy: e.target.value }))}
                className="col-span-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddRisk}
                className="px-4 py-1.5 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
              >
                Add Risk
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewRisk({ ...EMPTY_RISK }); }}
                className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Table header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
          <div className="col-span-1"></div>
          <div className="col-span-3">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Likelihood</div>
          <div className="col-span-2">Impact</div>
          <div className="col-span-1">Score</div>
          <div className="col-span-1"></div>
        </div>

        {/* Risk rows */}
        {risks.map((risk) => {
          const dot = getStatusDot(risk);
          const isExpanded = expandedRiskId === risk.id;

          return (
            <div key={risk.id} className="border-b border-gray-100 last:border-b-0">
              {/* Main row */}
              <div
                className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50 cursor-pointer"
                onClick={() => setExpandedRiskId(isExpanded ? null : risk.id)}
              >
                <div className="col-span-1 flex items-center gap-2">
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${dot.color}`}
                    title={dot.label}
                  />
                  {isExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </div>
                <div className="col-span-3 text-sm font-medium text-gray-900 truncate" title={risk.title}>
                  {risk.title}
                </div>
                <div className="col-span-2">
                  <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {risk.category.replace('_', ' ')}
                  </span>
                </div>
                <div className="col-span-2">
                  {!isApproved ? (
                    <select
                      value={editingFields[risk.id]?.likelihood ?? risk.likelihood}
                      onChange={(e) => handleFieldChange(risk.id, 'likelihood', e.target.value)}
                      onBlur={() => handleFieldBlur(risk.id, 'likelihood')}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs px-2 py-1 border border-gray-200 rounded w-full"
                    >
                      {LIKELIHOOD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-gray-600">
                      {LIKELIHOOD_OPTIONS.find((o) => o.value === risk.likelihood)?.label}
                    </span>
                  )}
                </div>
                <div className="col-span-2">
                  {!isApproved ? (
                    <select
                      value={editingFields[risk.id]?.impact ?? risk.impact}
                      onChange={(e) => handleFieldChange(risk.id, 'impact', e.target.value)}
                      onBlur={() => handleFieldBlur(risk.id, 'impact')}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs px-2 py-1 border border-gray-200 rounded w-full"
                    >
                      {IMPACT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs text-gray-600">
                      {IMPACT_OPTIONS.find((o) => o.value === risk.impact)?.label}
                    </span>
                  )}
                </div>
                <div className="col-span-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getRiskScoreColor(risk.risk_score)}`}>
                    {risk.risk_score}
                  </span>
                </div>
                <div className="col-span-1 flex justify-end">
                  {!isApproved && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteRisk(risk.id); }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete risk"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-4 pb-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4 ml-7">
                    {/* Description */}
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
                      {!isApproved ? (
                        <textarea
                          defaultValue={risk.description}
                          onBlur={(e) => {
                            if (e.target.value !== risk.description) {
                              handleUpdateRisk(risk.id, { description: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                          rows={2}
                        />
                      ) : (
                        <p className="text-sm text-gray-700">{risk.description}</p>
                      )}
                    </div>

                    {/* Mitigation */}
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Mitigation Strategy</label>
                      {!isApproved ? (
                        <textarea
                          defaultValue={risk.mitigation_strategy}
                          onBlur={(e) => {
                            if (e.target.value !== risk.mitigation_strategy) {
                              handleUpdateRisk(risk.id, { mitigation_strategy: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md"
                          rows={2}
                        />
                      ) : (
                        <p className="text-sm text-gray-700">{risk.mitigation_strategy}</p>
                      )}
                    </div>

                    {/* Linked Controls */}
                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-500">Linked Controls</label>
                        <button
                          onClick={() => handleLoadSuggestions(risk.id)}
                          className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
                        >
                          <Link2 className="w-3 h-3" />
                          Suggest
                        </button>
                      </div>
                      {risk.mapped_control_ids?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {risk.mapped_control_ids.map((taskId) => (
                            <span
                              key={taskId}
                              className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full flex items-center gap-1"
                            >
                              {taskId.substring(0, 8)}...
                              {!isApproved && (
                                <button
                                  onClick={() => {
                                    const updated = risk.mapped_control_ids.filter((id) => id !== taskId);
                                    handleUpdateRisk(risk.id, { mapped_control_ids: updated });
                                  }}
                                  className="text-teal-500 hover:text-teal-700"
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                      {suggestedControls[risk.id]?.length > 0 && (
                        <div className="space-y-1">
                          {suggestedControls[risk.id].map((s) => (
                            <div
                              key={s.task_id}
                              className="flex items-center justify-between text-xs bg-blue-50 px-2 py-1 rounded"
                            >
                              <span className="text-gray-700 truncate">
                                [{s.control_id}] {s.title}
                              </span>
                              {!isApproved && !risk.mapped_control_ids?.includes(s.task_id) && (
                                <button
                                  onClick={() => {
                                    const updated = [...(risk.mapped_control_ids || []), s.task_id];
                                    handleUpdateRisk(risk.id, { mapped_control_ids: updated });
                                  }}
                                  className="text-blue-600 hover:text-blue-800 ml-2 shrink-0"
                                >
                                  + Link
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {risks.length === 0 && !generating && (
          <div className="px-4 py-8 text-center text-gray-500">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No risks yet. Risks are being generated...</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
          AI-generated, unmodified
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          Modified
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
          User-added
        </div>
      </div>
    </div>
  );
};

export default RiskAssessmentPage;
